/* <club-signup>: the one sign-up (snippets/signup.liquid) in the footer, the club panel, the club page and «Gi meg
   beskjed», one field at a time as on live: the e-mail field (its button and the terms line show once something is
   typed) → «Fullfør registreringen» (mobile with SMS consent) → «Få gave!» (birthday, optional) → «Takk!». The footer
   is only the way in: «Bli med» opens the club panel at «Fullfør registreringen». With Klaviyo's public key and list
   from the theme settings it runs against Klaviyo's client API; without them it stays Shopify's own form.
   What Klaviyo gets:
   1. Nothing for the club's e-mail step (the address waits for the consent). «Gi meg beskjed» puts the variant on its
      back-in-stock list (e-mail); with mobile and consent in its own form the list also gets the SMS channel and the
      number (Sondre 02.11), and 2 follows in the same press.
   2. One subscription to the list with the address, the mobile number and the consent for e-mail and SMS. The
      profile is marked signup_type customer_club: «10% kundeklubb» starts on the SMS consent, for those only.
   3. The birthday on the profile, if it is given.
   Every sign-up on the page shares one state, so the footer follows the panel. This browser remembers the address and
   the membership, so a member is not asked again. Nothing is sent before a step is submitted. */
const API = 'https://a.klaviyo.com/client';
// The revision that takes the consent block (2024-10-15 refuses it with 400).
const REVISION = '2025-01-15';
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MOBILE = /^[49]\d{7}$/;
const SAVED = 'laley:club';
const DISMISSED = 'laley:club-badge';
// Shared with the current theme: changing theme must not ask someone who already answered again.
const ANSWERED = 'kk_popup_answered';
const AUTO_SHOWN = 'kk_shown_v4';
function rememberAnswered() {
  try {
    localStorage.setItem(ANSWERED, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
  } catch {
    /* private mode: the exposure is still remembered for this page */
  }
}
if (new URLSearchParams(location.search).has('_kx')) rememberAnswered();

/** step 1: no address yet, 2: address given, 3: member (birthday asked), 4: done. Only membership outlives a visit. */
const club = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED)) || {};
    return { step: saved.step >= 3 ? saved.step : 1, email: saved.email || '' };
  } catch {
    return { step: 1, email: '' };
  }
})();
function save(changes, source) {
  Object.assign(club, changes);
  if (club.step >= 3) rememberAnswered();
  try {
    localStorage.setItem(SAVED, JSON.stringify(club));
  } catch {
    /* private mode: the steps still work for this page */
  }
  document.dispatchEvent(new CustomEvent('club:change', { detail: { source } }));
}

/** DD.MM, or four digits from a phone's number pad; the date has to exist (29.02 does). */
function parseBirthday(value) {
  const [d, m] = /\D/.test(value)
    ? value.split(/\D+/).map(Number)
    : [Number(value.slice(0, 2)), Number(value.slice(2, 4))];
  if (!(m >= 1 && m <= 12 && d >= 1 && new Date(2000, m - 1, d).getMonth() === m - 1)) return null;
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  // Bursdag/-dag/-maned as the club has written them; Birthday is the date «Happy Birthday» runs on every year, so the
  // year (2000, a leap year) is not the person's.
  return { Bursdag: `${dd}.${mm}`, Bursdagsdag: d, Bursdagsmaned: m, Birthday: `2000-${mm}-${dd}` };
}

// Where a sign-up that continues in the panel started (the footer), for Klaviyo's source; the panel takes it once.
let via = '';

class ClubSignup extends HTMLElement {
  get ready() {
    return !!(this.dataset.key && this.dataset.list);
  }

  // Little at start: without Klaviyo the shop's form works on its own (CSS shows its button); with Klaviyo the form
  // is swapped (a few nodes moved, under a millisecond) and the step the visitor is at is shown.
  connectedCallback() {
    // Once something is typed the button and the lines stay, also if the field is emptied again (as V3).
    this.addEventListener('input', ({ target }) => {
      if (target.type === 'email' && target.value) target.form.dataset.typed = '';
    });
    if (!this.ready) return;
    this.wish = this.querySelector('form[data-wish]');
    this.notify = !!this.wish;
    this.takeOver();
    this.via = via;
    via = '';
    this.listed = false;
    this.clubRequested = false;
    this.addEventListener('submit', this);
    this.addEventListener('click', this);
    this.follow = (event) => this.render(event.detail.source === this);
    document.addEventListener('club:change', this.follow);
    this.render();
  }

  disconnectedCallback() {
    if (this.follow) document.removeEventListener('club:change', this.follow);
  }

  /** With Klaviyo the e-mail step is ours: Shopify's own form is re-sent by the shop's bot check even when the
     submit is stopped, so it is swapped for a plain form with the same field, button and lines. */
  takeOver() {
    const shop = this.querySelector('form.club-form[action]');
    if (!shop) return;
    const form = document.createElement('form');
    form.className = shop.className;
    form.dataset.step = '1';
    form.noValidate = true;
    form.append(...shop.querySelectorAll(':scope > :not([type=hidden])'));
    shop.replaceWith(form);
  }

  handleEvent(event) {
    if (event.type === 'submit') {
      event.preventDefault();
      this.submit(event.target);
      return;
    }
    const invitation = event.target.closest('[data-notify-club]');
    if (invitation) {
      this.clubRequested = !this.clubRequested;
      this.render(this.clubRequested);
      if (!this.clubRequested) invitation.focus();
      return;
    }
    if (event.target.closest('[data-back]')) save({ step: 1 }, this);
    if (event.target.closest('[data-skip]')) save({ step: 4 }, this);
  }

  /** The step to show: «Gi meg beskjed» shows its own e-mail field until the product is on the list, then the
     club's steps, unless the visitor was a member already. */
  get shown() {
    // The footer stays the way in: its e-mail step until the visitor is a member, then the thanks.
    if (this.dataset.continues) return club.step >= 3 ? 4 : 1;
    if (!this.notify) return club.step;
    if (!this.listed) return 0;
    return this.wasMember || !this.clubRequested ? 0 : club.step;
  }

  render(focus = false) {
    if (this.notify && club.step === 1) this.listed = false;
    if (this.notify && !this.listed) this.clubRequested = false;
    const shown = this.shown;
    this.querySelectorAll('[data-step]').forEach((step) => {
      step.hidden = Number(step.dataset.step) !== shown;
    });
    if (this.notify) {
      this.wish.hidden = this.listed;
      // A member gives the address alone; the club's fields in the alert's own form are for everyone else.
      this.wish.querySelectorAll('[data-wish-club]').forEach((field) => {
        field.hidden = club.step >= 3;
      });
      this.querySelectorAll('[data-listed]').forEach((part) => {
        part.hidden = !this.listed;
      });
      this.querySelector('[data-notify-membership]').hidden = !this.listed || !this.clubRequested || this.wasMember;
      this.querySelector('[data-notify-continue]').classList.toggle('button--primary', shown === 0 || shown === 4);
    }
    // A stock alert is complete on its own. Its club invitation under the confirmation opens the existing consent flow.
    const offer = this.querySelector('[data-offer]');
    if (offer) {
      offer.hidden = this.notify ? !(this.listed && !this.wasMember && club.step === 2) : shown > 2;
      if (this.notify) offer.setAttribute('aria-expanded', this.clubRequested);
    }
    this.querySelectorAll('[data-email]').forEach((target) => {
      target.textContent = club.email;
    });
    this.querySelectorAll('form [type=email]').forEach((input) => {
      if (!input.value) input.value = club.email;
    });
    if (!focus) return;
    const current = this.notify && !this.listed ? this.wish : this.querySelector(`[data-step="${shown}"]`);
    // Each step's first field: the address again after «Endre», the mobile, the birthday.
    (current?.querySelector('input') || current)?.focus();
  }

  fail(form, kind, control) {
    form.querySelector('[data-error]').textContent = this.dataset[`error${kind[0].toUpperCase()}${kind.slice(1)}`];
    if (!control) return;
    control.setAttribute('aria-invalid', 'true');
    control.focus();
  }

  async send(path, data) {
    const request = new AbortController();
    let timeout;
    try {
      // A dropped mobile connection must release the action. Never repeat an uncertain subscription automatically.
      const expired = new Promise((_, reject) => {
        timeout = setTimeout(() => {
          request.abort();
          reject(new Error('Klaviyo timeout'));
        }, 15000);
      });
      const response = await Promise.race([
        fetch(`${API}/${path}/?company_id=${encodeURIComponent(this.dataset.key)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
          body: JSON.stringify({ data }),
          signal: request.signal,
        }),
        expired,
      ]);
      // An implementation that resolves while aborting must not advance the form after its deadline.
      if (request.signal.aborted) throw new Error('Klaviyo timeout');
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
    } finally {
      clearTimeout(timeout);
    }
  }

  async submit(form) {
    const button = form.querySelector('[type=submit]');
    if (button.getAttribute('aria-busy') === 'true') return;
    form.querySelector('[data-error]').textContent = '';
    form.querySelectorAll('[aria-invalid]').forEach((control) => control.removeAttribute('aria-invalid'));
    const step = form === this.wish ? 0 : Number(form.dataset.step);
    try {
      button.setAttribute('aria-busy', 'true');
      if (step === 0) await this.list(form);
      if (step === 1) this.email(form);
      if (step === 2) await this.member(form);
      if (step === 3) await this.birthday(form);
    } catch {
      this.fail(form, 'send');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** «Gi meg beskjed»: the address puts the variant on its list. */
  async list(form) {
    const picker = form.querySelector('[data-notify-variant-picker]');
    const variant = picker ? picker.value : this.dataset.variant;
    if (!/^\d+$/.test(variant || '') || (picker && ![...picker.options].some((option) => option.value === variant)))
      return this.fail(form, 'variant', picker);
    const input = form.querySelector('[type=email]');
    const email = input.value.trim();
    if (!EMAIL.test(email)) return this.fail(form, 'email', input);
    const joining = this.joining(form);
    if (joining === false) return;
    const wasMember = club.step >= 3;
    const phone = joining ? `+47${form.phone.value.replace(/[\s-]/g, '').replace(/^(\+|00)47/, '')}` : '';
    await this.send('back-in-stock-subscriptions', {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: phone ? ['EMAIL', 'SMS'] : ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email, ...(phone ? { phone_number: phone } : {}) } } },
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant}` } },
      },
    });
    save({ email, step: Math.max(club.step, 2) });
    // Sondre 29.10: mobile and consent in the alert's own form join the club in the same press.
    if (joining) await this.member(form);
    this.listed = true;
    this.clubRequested = false;
    this.wasMember = wasMember;
    this.render();
    // The confirmation is read first; the club's invitation waits under it.
    this.querySelector('.notify-listed-title').focus();
  }

  /** The club in the alert's form: the mobile and the consent go together. Neither: the alert alone (null). One
     without the other: the field is pointed out and nothing is sent (false). */
  joining(form) {
    if (!form.consent || form.consent.closest('[hidden]')) return null;
    const digits = form.phone.value.replace(/[\s-]/g, '').replace(/^(\+|00)47/, '');
    if (!digits && !form.consent.checked) return null;
    if (!MOBILE.test(digits)) {
      this.fail(form, 'phone', form.phone);
      return false;
    }
    if (!form.consent.checked) {
      this.fail(form, 'consent', form.consent);
      return false;
    }
    return true;
  }

  /** The address is kept for the consent; the footer hands it on to the club panel. */
  email(form) {
    const input = form.querySelector('[type=email]');
    const email = input.value.trim();
    if (!EMAIL.test(email)) return this.fail(form, 'email', input);
    if (!this.dataset.continues) return save({ email, step: Math.max(club.step, 2) }, this);
    via = this.dataset.where;
    save({ email, step: Math.max(club.step, 2) });
    window.Laley.openPanel(this.dataset.continues, { trigger: form.querySelector('[type=submit]') });
    document.querySelector('#panel-body club-signup [name=phone]')?.focus();
  }

  async member(form) {
    const digits = form.phone.value.replace(/[\s-]/g, '').replace(/^(\+|00)47/, '');
    if (!MOBILE.test(digits)) return this.fail(form, 'phone', form.phone);
    if (!form.consent.checked) return this.fail(form, 'consent', form.consent);
    const phone = `+47${digits}`;
    const now = new Date().toISOString();
    await this.send('subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: `Tema · ${this.via || this.dataset.where}`,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: club.email,
              phone_number: phone,
              subscriptions: {
                email: { marketing: { consent: 'SUBSCRIBED' } },
                sms: { marketing: { consent: 'SUBSCRIBED' } },
              },
              properties: {
                signup_type: 'customer_club',
                signup_location: location.pathname,
                // The guard segment «Ba om SMS, men mangler SMS-samtykke» reads these three.
                laley_sms_requested: true,
                laley_sms_requested_at: now,
                laley_sms_phone_requested: phone,
                laley_membership_consent_text: form.querySelector('[data-consent-text]').textContent.trim(),
                laley_membership_consented_at: now,
              },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
    });
    // Said while the focus moves on to the birthday, as V3.
    this.querySelector('[data-said]').textContent = this.dataset.member;
    save({ step: 3 }, this);
  }

  async birthday(form) {
    const value = form.birthday.value.trim();
    if (value) {
      const properties = parseBirthday(value);
      if (!properties) return this.fail(form, 'birthday', form.birthday);
      await this.send('profiles', { type: 'profile', attributes: { email: club.email, properties } });
    }
    save({ step: 4 }, this);
  }
}

customElements.define('club-signup', ClubSignup);

/* The automatic offer is another view of the same panel and the same sign-up. Cookie choice and shopping come
   first; a timer or desktop exit intent only makes it ready, and events open it once the page is free. */
const autoView = document.querySelector('template[data-view="club-auto"]');
function watchAuto() {
  const signup = autoView.content.querySelector('club-signup');
  if (!signup?.dataset.key || !signup.dataset.list) return;
  const panel = document.getElementById('panel');
  let due = false;
  let exposed = false;
  const stored = (key, session = false) => {
    try {
      return (session ? sessionStorage : localStorage).getItem(key);
    } catch {
      return null;
    }
  };
  const knownMember = () => {
    if (club.step >= 3 || autoView.hasAttribute('data-known-customer')) return true;
    try {
      return Object.keys(localStorage).some(
        (key) => key.startsWith('kk_subscribed_') && Number(localStorage.getItem(key)) > Date.now(),
      );
    } catch {
      return false;
    }
  };
  const blocked = () =>
    exposed ||
    stored(AUTO_SHOWN, true) ||
    Number(stored(ANSWERED)) > Date.now() ||
    knownMember() ||
    document.querySelector('club-signup[data-where="page"], [data-own-signup]') ||
    /\/(?:cart|checkouts?|collections)(?:\/|$)/.test(location.pathname) ||
    /[?&](?:co_|filter\.|sort_by=|page=)/.test(location.search);
  const privacyPending = () => {
    const banner = document.querySelector('[data-consent-banner]');
    if (banner && !banner.hidden) return true;
    const privacy = window.Shopify?.customerPrivacy;
    if (banner && !privacy) return true;
    return !!(privacy?.shouldShowBanner() && !Object.values(privacy.currentVisitorConsent()).some(Boolean));
  };
  const attempt = () => {
    if (
      !due ||
      blocked() ||
      privacyPending() ||
      panel.open ||
      document.hidden ||
      document.querySelector('.site-header.is-searching') ||
      document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]), textarea, select')
    )
      return;
    window.Laley.openPanel('club-auto', { side: 'center' });
  };
  const timer = setTimeout(
    () => {
      due = true;
      attempt();
    },
    Number(autoView.dataset.clubAutoDelay) || 0,
  );
  const markExposed = () => {
    exposed = true;
    clearTimeout(timer);
    try {
      sessionStorage.setItem(AUTO_SHOWN, '1');
    } catch {
      /* the current page still remembers */
    }
  };
  panel.addEventListener('panel:open', () => {
    if (panel.dataset.view.startsWith('club')) markExposed();
  });
  // The completed stock alert already offers the club. Continuing to shop must not open another invitation.
  document.addEventListener('club:change', ({ detail }) => {
    if (detail.source?.notify && detail.source.listed) markExposed();
  });
  panel.addEventListener('close', () => {
    if (panel.dataset.view === 'club-auto') rememberAnswered();
    attempt();
  });
  document.addEventListener('consent:changed', attempt);
  document.addEventListener('visitorConsentCollected', attempt);
  document.addEventListener('visibilitychange', attempt);
  document.addEventListener('focusout', () => setTimeout(attempt, 0));
  if (autoView.dataset.clubAutoExit === 'true')
    document.addEventListener('mouseout', (event) => {
      if (
        event.relatedTarget ||
        event.clientY > 0 ||
        innerWidth <= 768 ||
        !matchMedia('(hover: hover) and (pointer: fine)').matches
      )
        return;
      due = true;
      attempt();
    });
}
if (autoView) watchAuto();

/* Tilbudsmerket: the offer in the corner opens the club panel once the first screen is passed. It steps aside for a
   member, once dismissed (this visit), where the page offers the club itself (the footer in view, the club page), on
   product pages (the buy bar has the corner) and over a product card (its heart and quick add).
   IntersectionObservers tell it; nothing is measured while scrolling, and nothing is set up before the browser is
   idle. */
const badge = document.querySelector('[data-club-badge]');
// A page that offers the club itself says so with [data-own-signup] (the club page's sign-up, an add-on's own form).
const away = document.querySelector('[data-product-page], club-signup[data-where="page"], [data-own-signup]');
function watchBadge() {
  const seen = { top: true, footer: false, cards: new Set(), dismissed: false };
  try {
    seen.dismissed = sessionStorage.getItem(DISMISSED) === '1';
  } catch {
    /* private mode: it comes back on the next page */
  }
  const update = () => {
    badge.hidden = club.step >= 3 || seen.dismissed || seen.top || seen.footer || seen.cards.size > 0;
  };
  const watch = (fn, options) =>
    new IntersectionObserver((entries) => {
      entries.forEach(fn);
      update();
    }, options);
  // A mark 60 % down the first screen: once it has passed above, so has the first screen.
  const mark = document.createElement('div');
  mark.className = 'club-badge-mark';
  mark.setAttribute('aria-hidden', 'true');
  document.body.append(mark);
  watch((entry) => {
    seen.top = entry.isIntersecting || entry.boundingClientRect.top > 0;
  }).observe(mark);
  const footer = document.querySelector('.site-footer');
  if (footer)
    watch((entry) => {
      seen.footer = entry.isIntersecting;
    }).observe(footer);
  // The cards that reach into the corner the badge takes (with room around it).
  let corner;
  const watchCards = () => {
    corner?.disconnect();
    seen.cards.clear();
    corner = watch((entry) => seen.cards[entry.isIntersecting ? 'add' : 'delete'](entry.target), {
      rootMargin: `${96 - innerHeight}px 0px 0px ${280 - innerWidth}px`,
    });
    document.querySelectorAll('[data-product-card]').forEach((card) => corner.observe(card));
  };
  watchCards();
  addEventListener('resize', watchCards);
  document.addEventListener('club:change', update);
  badge.querySelector('[data-club-dismiss]').addEventListener('click', () => {
    seen.dismissed = true;
    update();
    try {
      sessionStorage.setItem(DISMISSED, '1');
    } catch {
      /* private mode: hidden for this page */
    }
  });
}
// The badge waits for the first screen to pass anyway, so it is set up when the browser is idle, not at start.
const whenIdle = window.requestIdleCallback?.bind(window) || ((fn) => setTimeout(fn, 200));
if (badge && !away) whenIdle(watchBadge, { timeout: 3000 });
